import React from 'react';
import { Modal, Table, message, Spin } from 'antd';
import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { openSettlementPrintPreview } from '../utils/settlementPdfUtil';

/**
 * SettlementModal - Display and export settlement document
 * Shows financial summary and detailed breakdowns for a group
 */
const SettlementModal = ({ open, onClose, settlementData, loading }) => {
  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '0';
    const num = Math.trunc(Number(String(v).replace(/,/g, '')));
    if (!Number.isFinite(num)) return String(v);
    return num.toLocaleString('ko-KR');
  };

  const handlePrintPreview = async () => {
    try {
      await openSettlementPrintPreview(settlementData);
    } catch (error) {
      console.error('Print preview error:', error);
      message.error('인쇄 미리보기 중 오류가 발생했습니다.');
    }
  };

  const {
    groupName = '-',
    groupNo = '-',
    saleNo = '-',
    startDate = '-',
    endDate = '-',
    managerName = '-',
    revenue = 0,
    receivable = 0,
    preDeposit = 0,
    expectedProfit = 0,
    revenueBreakdown = [],
    receivableBreakdown = [],
    preDepositBreakdown = [],
    depositBreakdown = [],
    remark = '',
  } = settlementData || {};

  const safeReceivableBreakdown = Array.isArray(receivableBreakdown) ? receivableBreakdown : [];

  // Revenue breakdown table columns
  const revenueColumns = [
    {
      title: '주문번호',
      dataIndex: '주문번호',
      key: '주문번호',
      width: 120,
      align: 'center',
    },
    {
      title: '주문자',
      dataIndex: '주문자',
      key: '주문자',
      width: 140,
      align: 'center',
    },
    {
      title: '상품금액',
      dataIndex: '상품금액',
      key: '상품금액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '추가 판매가',
      dataIndex: '택스및유류',
      key: '택스및유류',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '프로모션할인',
      dataIndex: '프로모션할인',
      key: '프로모션할인',
      width: 140,
      align: 'right',
      render: (v) => {
        const num = Math.trunc(Number(String(v ?? '').replace(/,/g, '')));
        if (!Number.isFinite(num)) return <span className="tabular-nums">{String(v ?? '')}</span>;
        const displayAbs = fmtMoney(Math.abs(num));
        const signed = num < 0 ? `-${displayAbs}` : `-${displayAbs}`; // always show as negative visually
        return <span className="tabular-nums text-red-600">{signed}</span>;
      },
    },
    {
      title: '고객결제가',
      dataIndex: '고객결제가',
      key: '고객결제가',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Deposit (입금) list table columns
  const depositColumns = [
    {
      title: '수납일',
      dataIndex: '수납일',
      key: '수납일',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '송금(카드소유)자',
      dataIndex: '송금카드소유자',
      key: '송금카드소유자',
      width: 160,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '결제구분',
      dataIndex: '결제구분',
      key: '결제구분',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '계좌정보',
      dataIndex: '계좌카드번호',
      key: '계좌카드번호',
      width: 180,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '입금액',
      dataIndex: '입금액',
      key: '입금액',
      width: 140,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Receivable breakdown table columns
  const receivableColumns = [
    {
      title: '주문번호',
      dataIndex: '주문번호',
      key: '주문번호',
      width: 120,
      align: 'center',
    },
    {
      title: '주문자',
      dataIndex: '주문자',
      key: '주문자',
      width: 140,
      align: 'center',
    },
    {
      title: '고객결제가',
      dataIndex: '고객결제가',
      key: '고객결제가',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '입금액',
      dataIndex: '입금액',
      key: '입금액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '미수액',
      dataIndex: '미수액',
      key: '미수액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Pre-deposit breakdown table columns (requested fields)
  const preDepositColumns = [
    {
      title: '출납일',
      dataIndex: '매핑출납일',
      key: '매핑출납일',
      width: 140,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '지급액',
      dataIndex: '지급액',
      key: '지급액',
      width: 150,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '종류',
      dataIndex: '매핑종류',
      key: '매핑종류',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '결제구분',
      dataIndex: '매핑결제구분',
      key: '매핑결제구분',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '송금카드소유자',
      dataIndex: '매핑송금카드소유자',
      key: '매핑송금카드소유자',
      width: 160,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '계좌정보',
      dataIndex: '매핑계좌카드번호',
      key: '매핑계좌카드번호',
      width: 180,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '비고',
      dataIndex: '비고',
      key: '비고',
      width: 220,
      align: 'center',
      render: (v) => v || '-',
    },
  ];

  // Calculate summary for tables
  const calculateSum = (data, key) => {
    return data.reduce((acc, row) => {
      const raw = String(row?.[key] ?? '');
      const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
      const num = Number(cleaned);
      const val = Number.isFinite(num) ? num : 0;
      return acc + val;
    }, 0);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      title={
        <div className="flex items-center gap-2">
          <FileTextOutlined className="text-gray-800" />
          <span className="font-semibold text-gray-900">정산서</span>
        </div>
      }
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            발행일: {new Date().toLocaleDateString('ko-KR')}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              닫기
            </button>
            <button
              onClick={handlePrintPreview}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <PrinterOutlined />
              인쇄 미리보기
            </button>
          </div>
        </div>
      }
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" tip="정산 정보를 불러오는 중..." />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Document Info */}
          <div className="bg-gray-50 border border-gray-300 rounded p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex">
                <span className="font-semibold text-gray-700 min-w-[100px]">단체명:</span>
                <span className="text-gray-900">{groupName}</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 min-w-[100px]">단체번호:</span>
                <span className="text-gray-900">{groupNo}</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 min-w-[100px]">판매번호:</span>
                <span className="text-gray-900">{saleNo}</span>
              </div>
              <div className="flex">
                <span className="font-semibold text-gray-700 min-w-[100px]">담당자:</span>
                <span className="text-gray-900">{managerName}</span>
              </div>
              <div className="flex col-span-2">
                <span className="font-semibold text-gray-700 min-w-[100px]">여행기간:</span>
                <span className="text-gray-900">
                  {startDate && endDate ? `${startDate} ~ ${endDate}` : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-3 pb-2 border-b-2 border-gray-400">
              정산 요약
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-gray-300 rounded p-3 bg-white">
                <div className="text-xs font-semibold text-gray-600 mb-2">행사 매출액</div>
                <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                  {fmtMoney(revenue)} 원
                </div>
              </div>
              <div className="border border-gray-300 rounded p-3 bg-white">
                <div className="text-xs font-semibold text-gray-600 mb-2">행사 미수금</div>
                <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                  {fmtMoney(receivable)} 원
                </div>
              </div>
              <div className="border border-gray-300 rounded p-3 bg-white">
                <div className="text-xs font-semibold text-gray-600 mb-2">행사 전도금</div>
                <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                  {fmtMoney(preDeposit)} 원
                </div>
              </div>
              <div className="border border-gray-300 rounded p-3 bg-white">
                <div className="text-xs font-semibold text-gray-600 mb-2">행사 예상수익</div>
                <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                  {fmtMoney(expectedProfit)} 원
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          {Array.isArray(revenueBreakdown) && revenueBreakdown.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                매출액 상세 내역
              </h3>
              <Table
                size="small"
                columns={revenueColumns}
                dataSource={revenueBreakdown}
                rowKey={(row, idx) => `revenue-${row?.주문번호 || idx}`}
                pagination={false}
                bordered
                scroll={{ x: 650, y: 300 }}
                summary={(pageData) => {
                  const total상품 = calculateSum(pageData, '상품금액');
                  const total유류 = calculateSum(pageData, '택스및유류');
                  // For promotion discount, subtract positive values (treat as negative contribution)
                  const total할인Raw = pageData.reduce((acc, row) => {
                    const raw = String(row?.['프로모션할인'] ?? '');
                    const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
                    const num = Number(cleaned);
                    const val = Number.isFinite(num) ? (num > 0 ? -num : num) : 0;
                    return acc + val;
                  }, 0);
                  const total결제 = calculateSum(pageData, '고객결제가');
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2} align="center">
                          <span className="font-semibold">합계</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total상품)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total유류)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span className="tabular-nums font-semibold text-red-600">{fmtMoney(total할인Raw)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total결제)}</span>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}

          {/* Deposit List (입금 리스트) */}
          {Array.isArray(depositBreakdown) && depositBreakdown.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                입금 리스트
              </h3>
              <Table
                size="small"
                columns={depositColumns}
                dataSource={depositBreakdown}
                rowKey={(row, idx) => `deposit-${row?.거래일련번호 || idx}`}
                pagination={false}
                bordered
                scroll={{ x: 600, y: 300 }}
                summary={(pageData) => {
                  const total입금 = calculateSum(pageData, '입금액');
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4} align="center">
                          <span className="font-semibold">합계</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total입금)}</span>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}

          {/* Receivable Breakdown */}
          {safeReceivableBreakdown.length > 0 ? (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                미수금 상세 내역
              </h3>
              <Table
                size="small"
                columns={receivableColumns}
                dataSource={safeReceivableBreakdown}
                rowKey={(row, idx) => `receivable-${row?.주문번호 || idx}`}
                pagination={false}
                bordered
                scroll={{ x: 650, y: 300 }}
                summary={(pageData) => {
                  const total결제 = calculateSum(pageData, '고객결제가');
                  const total입금 = calculateSum(pageData, '입금액');
                  const total미수 = calculateSum(pageData, '미수액');
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2} align="center">
                          <span className="font-semibold">합계</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total결제)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total입금)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total미수)}</span>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                미수금 상세 내역
              </h3>
              <div className="border border-gray-200 rounded px-3 py-4 bg-gray-50 text-sm text-gray-600">
                미수금 없음
              </div>
            </div>
          )}

          {/* Pre-Deposit Breakdown */}
          {Array.isArray(preDepositBreakdown) && preDepositBreakdown.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                전도금 상세 내역
              </h3>
              <Table
                size="small"
                columns={preDepositColumns}
                dataSource={preDepositBreakdown}
                rowKey={(row, idx) => `predeposit-${row?.세부내역번호 || idx}`}
                pagination={false}
                bordered
                scroll={{ x: 520, y: 300 }}
                summary={(pageData) => {
                  const total = calculateSum(pageData, '지급액');
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} align="center">
                          <span className="font-semibold">합계</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} align="right">
                          <span className="tabular-nums font-semibold">{fmtMoney(total)}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} colSpan={3} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          )}

          {/* Remark */}
          {remark && String(remark).trim() && (
            <div className="bg-gray-50 border border-gray-300 rounded p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">비고</h3>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{String(remark)}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default SettlementModal;
